import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card, CardContent, Input } from '@mlm/ui';
import {
  createPurchaseOrder,
  listPurchaseOrders,
  listSupplierCatalog,
  listSuppliers,
  type PurchaseOrderStatus,
  type SupplierCatalogItem,
} from '../lib/api';

const STATUS_TABS: Array<{ key: PurchaseOrderStatus | 'all'; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'incomplete', label: 'Incomplete' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

export function StatusBadge({ status }: { status: PurchaseOrderStatus }) {
  const cls: Record<PurchaseOrderStatus, string> = {
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-blue-100 text-blue-800',
    incomplete: 'bg-teal-100 text-teal-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-gray-200 text-gray-600',
  };
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium uppercase ${cls[status]}`}>
      {status}
    </span>
  );
}

export function PurchaseOrdersPage() {
  const [tab, setTab] = useState<PurchaseOrderStatus | 'all'>('all');
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['purchase-orders', tab],
    queryFn: () => listPurchaseOrders(tab === 'all' ? {} : { status: tab }),
  });
  const rows = query.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Purchase Orders</h1>
        <Button onClick={() => navigate('/purchase-orders/new')}>+ Create purchase order</Button>
      </div>

      <div className="flex flex-wrap gap-1 border-b">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 text-sm ${
              tab === t.key
                ? 'border-b-2 border-primary font-medium text-foreground'
                : 'text-muted-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {query.isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No purchase orders.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">PO #</th>
                  <th className="px-3 py-2 font-medium">Supplier</th>
                  <th className="px-3 py-2 font-medium">Items</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Created by</th>
                  <th className="px-3 py-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-muted/20">
                    <td className="px-3 py-2">
                      <Link to={`/purchase-orders/${r.id}`} className="font-medium text-primary">
                        {r.poNumber}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{r.supplierName}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.itemCount} item(s)</td>
                    <td className="px-3 py-2">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{r.createdByName ?? '—'}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {new Date(r.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

type DraftLine = {
  key: string; // productSupplierId
  productId: string;
  variantId: string | null;
  label: string;
  qty: string;
  unitCost: string;
};

export function CreatePurchaseOrderPage() {
  const navigate = useNavigate();
  const suppliersQ = useQuery({ queryKey: ['suppliers', 'active'], queryFn: () => listSuppliers(true) });
  const [supplierId, setSupplierId] = useState('');
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [pickKey, setPickKey] = useState('');

  const catalogQ = useQuery({
    queryKey: ['supplier-catalog', supplierId],
    queryFn: () => listSupplierCatalog(supplierId),
    enabled: !!supplierId,
  });
  const catalog = catalogQ.data ?? [];
  const catalogByKey = useMemo(
    () => new Map(catalog.map((c) => [c.productSupplierId, c])),
    [catalog],
  );

  const create = useMutation({
    mutationFn: () =>
      createPurchaseOrder({
        supplierId,
        memo: memo.trim() || null,
        lines: lines.map((l) => ({
          productId: l.productId,
          variantId: l.variantId,
          qtyOrdered: Number(l.qty),
          unitCost: l.unitCost,
        })),
      }),
    meta: { successMessage: 'Purchase order created.' },
    onSuccess: (po) => navigate(`/purchase-orders/${po.id}`),
  });

  function addLine() {
    const item = catalogByKey.get(pickKey);
    if (!item) return;
    if (lines.some((l) => l.key === item.productSupplierId)) return;
    setLines((prev) => [...prev, draftFromCatalog(item)]);
    setPickKey('');
  }

  const canSubmit =
    !!supplierId && lines.length > 0 && lines.every((l) => Number(l.qty) > 0) && !create.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Create Purchase Order</h1>
        <Link to="/purchase-orders" className="text-sm text-primary">
          ← Back to list
        </Link>
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Supplier</label>
            <select
              value={supplierId}
              onChange={(e) => {
                setSupplierId(e.target.value);
                setLines([]);
              }}
              className="h-9 w-full max-w-md rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">— Select supplier —</option>
              {(suppliersQ.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.code})
                </option>
              ))}
            </select>
          </div>

          {supplierId && (
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground">Add item</label>
              <div className="flex flex-wrap items-end gap-2">
                <select
                  value={pickKey}
                  onChange={(e) => setPickKey(e.target.value)}
                  className="h-9 min-w-[18rem] flex-1 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">
                    {catalog.length === 0
                      ? '— This supplier sources no products yet —'
                      : '— Select item —'}
                  </option>
                  {catalog.map((c) => (
                    <option key={c.productSupplierId} value={c.productSupplierId}>
                      {itemLabel(c)} — ₱{c.unitCost}
                    </option>
                  ))}
                </select>
                <Button type="button" variant="outline" onClick={addLine} disabled={!pickKey}>
                  + Add
                </Button>
              </div>
            </div>
          )}

          {lines.length > 0 && (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-2">Item</th>
                  <th className="py-1 pr-2">Qty</th>
                  <th className="py-1 pr-2">Unit cost</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.key} className="border-t">
                    <td className="py-1 pr-2">{l.label}</td>
                    <td className="py-1 pr-2">
                      <Input
                        className="h-8 w-20"
                        value={l.qty}
                        inputMode="numeric"
                        onChange={(e) =>
                          setLines((prev) =>
                            prev.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)),
                          )
                        }
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <Input
                        className="h-8 w-24"
                        value={l.unitCost}
                        inputMode="decimal"
                        onChange={(e) =>
                          setLines((prev) =>
                            prev.map((x, j) => (j === i ? { ...x, unitCost: e.target.value } : x)),
                          )
                        }
                      />
                    </td>
                    <td className="py-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}
                      >
                        ✕
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Memo</label>
            <textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              className="min-h-16 w-full rounded-md border border-input bg-background p-2 text-sm"
              placeholder="Notes for this order…"
            />
          </div>

          {create.error != null && (
            <p className="text-sm text-destructive">
              {(create.error as { response?: { data?: { error?: string } } })?.response?.data
                ?.error ?? 'Failed to create'}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate('/purchase-orders')}>
              Cancel
            </Button>
            <Button disabled={!canSubmit} onClick={() => create.mutate()}>
              {create.isPending ? 'Creating…' : 'Create purchase order'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function itemLabel(c: SupplierCatalogItem): string {
  return c.variantName ? `${c.productName} — ${c.variantName}` : c.productName;
}
function draftFromCatalog(c: SupplierCatalogItem): DraftLine {
  return {
    key: c.productSupplierId,
    productId: c.productId,
    variantId: c.variantId,
    label: itemLabel(c),
    qty: '1',
    unitCost: c.unitCost,
  };
}
